import { CalendarIcon, ChevronUpIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Flex, Popover, TextField } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDateWidget } from 'components/common/form/date/date-widget';
import {
  CommonTimeWidget,
  NEAREST_MINUTE,
} from 'components/common/form/date/time-widget';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { addHours, endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import enCA from 'date-fns/locale/en-CA';
import { t } from 'i18next';
import { getTimeFromDate, IDateRangeInput } from 'interfaces/forms/date';
import {
  LOCAL_DATE_FORMAT,
  LOCAL_DATETIME_FORMAT,
  LOCAL_DATETIME_TIMEZONE_FORMAT,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { clone } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

const SHOW_TIMEZONE = true;

// TODO: Make this a purely controlled component
export const CommonDateRangeInput = (props: IDateRangeInput) => {
  const trigger = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);

  const [startDate, setStartDate] = useState(props.defaultStartValue);
  const [endDate, setEndDate] = useState(props.defaultEndValue);

  const startOfDayRef = useRef(startOfDay(new Date()));
  const endOfDayRef = useRef(endOfDay(new Date()));

  const changeStartDate = (value: Date | undefined) => {
    setStartDate(value);

    if (!props.onChangeOnClose) {
      props.onChange({
        start: value,
        end: endDate,
      });
    }
  };

  const changeEndDate = (value: Date | undefined) => {
    setEndDate(value);

    if (!props.onChangeOnClose) {
      props.onChange({
        start: startDate,
        end: value,
      });
    }
  };

  const resetDates = () => {
    setStartDate(undefined);
    setEndDate(undefined);

    props.onChange({
      start: undefined,
      end: undefined,
    });
  };

  const getDisplayDate = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return '';
      }

      if (props.displayFormat) {
        return format(date, props.displayFormat, {
          locale: enCA,
        });
      }

      if (props.showTime && SHOW_TIMEZONE) {
        // full, with timezone; locale is necessary to ensure TZ prints as the abbreviations instead of GMT[+/-]#
        return format(date, LOCAL_DATETIME_TIMEZONE_FORMAT, {
          locale: enCA,
        });
      }

      if (props.showTime) {
        // only time, no timezone
        return format(date, LOCAL_DATETIME_FORMAT);
      }

      // only date, no time or timezone
      return format(date, LOCAL_DATE_FORMAT);
    },
    [props.displayFormat, props.showTime]
  );

  const displayStartValue = useMemo(
    () => getDisplayDate(startDate),
    [startDate, getDisplayDate]
  );

  const displayEndValue = useMemo(
    () => getDisplayDate(endDate),
    [endDate, getDisplayDate]
  );

  const triggerIconContent = () => {
    if (props.optional && (startDate || endDate)) {
      return (
        <Cross2Icon
          onClick={() => {
            if (props.disabled) {
              return;
            }

            resetDates();
          }}
        />
      );
    }

    if (open) {
      return <ChevronUpIcon />;
    }

    return (
      <CalendarIcon
        onClick={() => {
          if (props.disabled) {
            return;
          }

          setOpen(true);
        }}
      />
    );
  };

  const displayValue =
    displayStartValue && displayEndValue
      ? `${displayStartValue} - ${displayEndValue}`
      : '';

  const defaultStartTime = props.defaultStartValue
    ? getTimeFromDate(props.defaultStartValue, NEAREST_MINUTE)
    : // midnight
      getTimeFromDate(startOfDayRef.current, NEAREST_MINUTE);

  const defaultEndTime = props.defaultEndValue
    ? getTimeFromDate(props.defaultEndValue, NEAREST_MINUTE)
    : // 11:55 PM
      getTimeFromDate(endOfDayRef.current, NEAREST_MINUTE);

  // * Need to hide time inputs until we have both dates selected due to issues with syncing multiple non-controlled components and default values
  // * Will be unnecessary once this is re-written with a 3rd party library
  const showTimeInputs = props.showTime && startDate && endDate;

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Popover.Root
        open={open}
        onOpenChange={(value) => {
          if (props.disabled) {
            return;
          }

          setOpen(value);

          if (!value && props.onChangeOnClose) {
            props.onChange({
              start: startDate,
              end: endDate,
            });
          }
        }}
      >
        <Popover.Trigger disabled={props.disabled}>
          <TextField.Root
            ref={trigger}
            size={props.size}
            className={`cursor-pointer ${props.className ?? ''}`}
            disabled={props.disabled}
            color={props.inputColor}
            name={props.name}
            placeholder={t(
              props.placeholder ?? 'common.select-date-placeholder'
            ).toString()}
            required={!props.optional}
            value={displayValue}
            onChange={() => {
              // onChange provided to suppress error re: value w/o onChange
            }}
            onFocus={() => trigger?.current?.blur()}
            type="text"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
          >
            <TextField.Slot side="right">{triggerIconContent()}</TextField.Slot>
          </TextField.Root>
        </Popover.Trigger>
        <Popover.Content className="BorderedContent" maxWidth="unset">
          <Flex gap={RADIX.FLEX.GAP.FORM}>
            {/* start date */}
            <Flex align="stretch" direction="column" gap={RADIX.FLEX.GAP.FORM}>
              <Flex justify="center">
                <CommonInputLabel {...props} label="Start" />
              </Flex>

              <CommonDateWidget
                minDate={props.minDate}
                maxDate={props.maxDate}
                allowedDates={props.allowedDates}
                defaultDate={props.defaultStartValue}
                onChangeDate={(v) => {
                  if (!v) {
                    changeStartDate(undefined);
                    return;
                  }

                  const nextValue = clone(startDate ?? v);

                  nextValue.setFullYear(v.getFullYear());
                  nextValue.setMonth(v.getMonth());
                  nextValue.setDate(v.getDate());

                  // assume 12:00 AM if we're going from no value to some value to give the timepicker a good starting point
                  if (!props.defaultStartValue) {
                    nextValue.setHours(startOfDayRef.current.getHours());
                    nextValue.setMinutes(startOfDayRef.current.getMinutes());
                  }

                  // consider a start date after an end date to be invalid
                  if (endDate && isAfter(nextValue, endDate)) {
                    return;
                  }

                  changeStartDate(nextValue);
                }}
              />

              {props.showTime && (
                <CommonTimeWidget
                  className={showTimeInputs ? '' : 'hidden'}
                  defaultTime={defaultStartTime}
                  onChangeTime={(v) => {
                    if (!startDate) {
                      NotifyHelper.warning({
                        message_md: `Please select a date first.`,
                        debug: props.skipSelectDateWarning,
                      });
                      return;
                    }

                    if (!v) {
                      changeStartDate(startOfDay(startDate));
                      return;
                    }

                    // reset all other time values to 0 before setting H and M
                    const raw = startOfDay(startDate);

                    raw.setHours(v.h);
                    raw.setMinutes(v.m);

                    // convert from minutes to negative hours
                    const local = -(new Date().getTimezoneOffset() / 60);
                    const delta = local - v.offset;

                    const adjusted = addHours(raw, delta);

                    // consider a start date after an end date to be invalid
                    if (endDate && isAfter(adjusted, endDate)) {
                      return;
                    }
                    changeStartDate(adjusted);
                  }}
                />
              )}
            </Flex>

            {/* end date */}
            <Flex align="stretch" direction="column" gap={RADIX.FLEX.GAP.FORM}>
              <Flex justify="center">
                <CommonInputLabel {...props} label="End" />
              </Flex>

              <CommonDateWidget
                minDate={props.minDate}
                maxDate={props.maxDate}
                allowedDates={props.allowedDates}
                defaultDate={props.defaultEndValue}
                onChangeDate={(v) => {
                  if (!v) {
                    changeEndDate(undefined);
                    return;
                  }

                  const nextValue = clone(endDate ?? v);

                  nextValue.setFullYear(v.getFullYear());
                  nextValue.setMonth(v.getMonth());
                  nextValue.setDate(v.getDate());

                  // assume 11:55 PM if we're going from no value to some value to give the timepicker a good starting point
                  if (!props.defaultEndValue) {
                    nextValue.setHours(endOfDayRef.current.getHours());
                    nextValue.setMinutes(endOfDayRef.current.getMinutes());
                  }

                  // consider an end date before an start date to be invalid
                  if (startDate && isBefore(nextValue, startDate)) {
                    return;
                  }

                  changeEndDate(nextValue);
                }}
              />

              {props.showTime && (
                <CommonTimeWidget
                  className={showTimeInputs ? '' : 'hidden'}
                  defaultTime={defaultEndTime}
                  onChangeTime={(v) => {
                    if (!endDate) {
                      NotifyHelper.warning({
                        message_md: `Please select a date first.`,
                        debug: props.skipSelectDateWarning,
                      });
                      return;
                    }

                    if (!v) {
                      changeEndDate(startOfDay(endDate));
                      return;
                    }

                    // reset all other time values to 0 before setting H and M
                    const raw = startOfDay(endDate);

                    raw.setHours(v.h);
                    raw.setMinutes(v.m);

                    // convert from minutes to negative hours
                    const local = -(new Date().getTimezoneOffset() / 60);
                    const delta = local - v.offset;

                    const adjusted = addHours(raw, delta);

                    // consider an end date before an start date to be invalid
                    if (startDate && isBefore(adjusted, startDate)) {
                      return;
                    }

                    changeEndDate(adjusted);
                  }}
                />
              )}
            </Flex>
          </Flex>
        </Popover.Content>
      </Popover.Root>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};
